/**
 * client/src/components/layout/nav-lock-glyph.tsx
 *
 * The AAL lock affordance on a nav entry (AC-7, #1791) — one component,
 * rendered by BOTH the desktop sidebar and the mobile bottom tabs, so the two
 * can never drift in size, offset, or tooltip treatment (the same guarantee
 * `nav-badge.tsx` already gives the attention-count chip, and the reason a
 * prior fix that hand-copied the offset into each nav's own JSX could regress
 * silently — see sidebar.tsx's `.claude` history for the incident this
 * replaces).
 *
 * Two layout variants, matching where each nav has room:
 * - "overlay": pinned to the icon's own bottom-right corner (bottom tabs, and
 *   the collapsed/icon-only sidebar rail, which has no space for an inline
 *   sibling). MUST be rendered inside a `position: relative` ancestor — same
 *   contract `NavBadge`'s overlay className already assumes.
 * - "inline": a plain flex sibling pushed to the end of the row (the expanded
 *   sidebar, which has room for a labelled row).
 */

import { Lock } from "lucide-react";
import { cn } from "@/lib/cn";
import { MetricTooltip } from "@/components/ui/metric-tooltip";

export type NavLockGlyphVariant = "overlay" | "inline";

interface NavLockGlyphProps {
  /** Plain-language requirement shown in the tooltip (never a raw "AAL3" string). */
  label: React.ReactNode;
  variant: NavLockGlyphVariant;
}

/**
 * Exported so the regression tests for BOTH call sites (sidebar + bottom
 * tabs) assert against this one definition instead of re-typing copies of
 * the class list — a hard-coded copy in a test file is exactly the kind of
 * silent-drift risk this component exists to eliminate from the app code.
 */
export const NAV_LOCK_GLYPH_SIZE: Record<NavLockGlyphVariant, number> = {
  overlay: 11,
  inline: 13,
};
export const NAV_LOCK_GLYPH_CLASSES: Record<NavLockGlyphVariant, readonly string[]> = {
  overlay: ["text-text-tertiary", "absolute", "-right-1", "-bottom-1"],
  inline: ["text-text-tertiary", "ml-auto", "shrink-0"],
};

export function NavLockGlyph({ label, variant }: NavLockGlyphProps): React.ReactElement {
  return (
    <MetricTooltip content={label}>
      <Lock
        size={NAV_LOCK_GLYPH_SIZE[variant]}
        className={cn(...NAV_LOCK_GLYPH_CLASSES[variant])}
        aria-hidden="true"
      />
    </MetricTooltip>
  );
}
